import React, { useMemo } from 'react';

import { Bar, Line } from 'recharts';

import { ComboChart, type ComboChartAxis } from 'src/component/ds/ui/comboChart';
import { type ChartConfig } from 'src/component/ds/ui/chart';
import { scaleEnergyAxis } from 'src/screen/v2/Protected/DashboardScreen/PotentialEnergySavings/format';
import { useTranslation } from 'src/i18n';


/* The bars are energy, the line is ambient temperature. */
export const ENERGY_KEY = 'kwh';
export const AMBIENT_KEY = 'ambient';

const ENERGY_AXIS = 'energy';
const AMBIENT_AXIS = 'ambient';

export interface EnergySeriesPoint {
  label: string;
  kwh: number | null;
  ambient: number | null;
}

export interface EnergyChartProps {
  series: EnergySeriesPoint[];
  showEnergy?: boolean;
  showAmbient?: boolean;
  className?: string;
}

/**
 * Energy drawn against ambient temperature, for the Potential Energy Savings card.
 *
 * kWh runs to the hundreds and ambient temp stays under 30, so a shared scale would
 * flatten the temperature line -- the v1 chart splits them the same way, temperature
 * left and kWh right.
 *
 * @param {EnergyChartProps} props see EnergyChartProps
 * @returns {JSX.Element} the chart
 */
export default function EnergyChart ({
  series,
  showEnergy = true,
  showAmbient = true,
  className,
  ...props
}: EnergyChartProps & React.ComponentProps<'div'>) {
  const { t } = useTranslation();

  const config: ChartConfig = useMemo(() => ({
    [AMBIENT_KEY]: { label: t('dashboard.ambientTemp'), color: 'var(--ds-chart-ambient)' },
    [ENERGY_KEY]: { label: t('dashboard.totalKwh'), color: 'var(--ds-chart-energy)' },
  }), [t]);

  const rows = useMemo(
    () => series.map((p) => ({ label: p.label, [ENERGY_KEY]: p.kwh, [AMBIENT_KEY]: p.ambient })),
    [series]
  );

  const axes: ComboChartAxis[] = useMemo(() => [
    { id: AMBIENT_AXIS, series: [{ key: AMBIENT_KEY }], unit: '°C', width: 52 },
    {
      id: ENERGY_AXIS,
      series: [{ key: ENERGY_KEY, scale: scaleEnergyAxis }],
      position: 'right',
      baselineAtZero: true,
      width: 68,
    },
  ], []);

  return (
    <ComboChart
      config={config}
      data={rows}
      axes={axes}
      xKey='label'
      className={className}
      {...props}
    >
      {() => (
        <>
          {showEnergy && (
            <Bar
              yAxisId={ENERGY_AXIS}
              dataKey={ENERGY_KEY}
              fill='var(--color-kwh)'
              radius={2}
              maxBarSize={78}
            />
          )}

          {showAmbient && (
            <Line
              yAxisId={AMBIENT_AXIS}
              type='monotone'
              dataKey={AMBIENT_KEY}
              stroke='var(--color-ambient)'
              strokeWidth={2}
              dot={false}
              connectNulls
            />
          )}
        </>
      )}
    </ComboChart>
  );
}
